import type { AppSettingsSchema } from "@/interfaces/config/AppSettingsSchema";
import type {
	AppBannerVariant,
	SearchBannerSettings,
} from "@/interfaces/config/SearchBannerSettings";
import { AppSettingsHelper } from "@/utils/AppSettingsHelper";
import Logger from "@/utils/Logger";

const isAppBannerVariant = (value: string): value is AppBannerVariant =>
	["info", "warning", "success", "error"].includes(value);

export class AppSettingsService {
	private static getConfigValue<T extends keyof AppSettingsSchema>(
		key: T,
	): NonNullable<AppSettingsSchema[T]> {
		const config = AppSettingsHelper.getRuntimeConfig();
		const value = config[key];

		if (value === undefined || value === null) {
			const err = new Error(`Configuration key "${String(key)}" is undefined`);
			Logger.error(`Missing config: ${String(key)}`, err, ["AppSettingsService"]);
			throw err;
		}

		Logger.debug(`Retrieved config value for ${String(key)}`, ["AppSettingsService"]);
		return value;
	}

	static getApiUrl(): string {
		const config = AppSettingsHelper.getRuntimeConfig();
		const useInternal = config.use_internal_api_url === "true";
		const apiUrl = useInternal ? config.api_internal_url : config.api_base_url;

		Logger.debug(`API URL determined: ${useInternal ? "internal" : "external"}`, [
			"AppSettingsService",
		]);
		return apiUrl;
	}

	static getInternalApiUrl(): string {
		Logger.debug("Retrieved internal API URL", ["AppSettingsService"]);
		return this.getConfigValue("api_internal_url");
	}

	static shouldUseInternalApi(): boolean {
		Logger.debug("Retrieved internal API URL flag", ["AppSettingsService"]);
		return this.getConfigValue("use_internal_api_url") === "true";
	}

	static getApiClientIdentifier(): string {
		Logger.debug("Retrieved APIM Client Identifier", ["AppSettingsService"]);
		return this.getConfigValue("api_client_identifier");
	}

	static getOpenIdAuthority(): string {
		Logger.debug("Retrieved OpenID authority", ["AppSettingsService"]);
		return this.getConfigValue("openid_authority");
	}

	static getPingClientId(): string {
		Logger.debug("Retrieved Ping client ID", ["AppSettingsService"]);
		return this.getConfigValue("ping_client_id");
	}

	static getOpenIdConfigUrl(): string {
		Logger.debug("Retrieved OpenID config URL", ["AppSettingsService"]);
		return this.getConfigValue("openid_configuration");
	}

	static getCostcoEnvironment(): string {
		const env = this.getConfigValue("costco_environment");
		Logger.info(`Environment determined: ${env}`, ["AppSettingsService"]);
		return env;
	}

	static getCloudEnvironment(): string {
		Logger.debug("Retrieved cloud environment", ["AppSettingsService"]);
		return this.getConfigValue("cloud_environment");
	}

	static getScmPortalBaseUrl(): string {
		Logger.debug("Retrieved SCM portal base URL", ["AppSettingsService"]);
		return this.getConfigValue("scm_portal_base_url");
	}

	static isOAuthLocalHostBypassEnabled(): boolean {
		Logger.debug("OAuth localhost bypass enable not configured, defaulting to 'false'", [
			"AppSettingsService",
		]);
		return false;
	}

	static isDevelopmentMode(): boolean {
		try {
			const env = this.getCostcoEnvironment();
			const isDev = ["local", "dev", "development"].includes(env.toLowerCase());
			Logger.debug(`Development mode: ${isDev}`, ["AppSettingsService"]);
			return isDev;
		} catch (err) {
			Logger.warn("Failed to determine development mode, defaulting to false", [
				"AppSettingsService",
			]);
			return false;
		}
	}

	static isProductionMode(): boolean | undefined {
		try {
			const env = this.getCostcoEnvironment();
			const isProd = ["prod", "production"].includes(env.toLowerCase());
			Logger.debug(`Production mode: ${isProd}`, ["AppSettingsService"]);
			return isProd;
		} catch (err) {
			Logger.warn("Failed to determine production mode, defaulting to true for safety", [
				"AppSettingsService",
			]);
		}
	}

	static validateCriticalConfig(): boolean {
		Logger.info("Validating critical configuration", ["AppSettingsService"]);
		try {
			const result = AppSettingsHelper.validateCriticalConfig();
			Logger.info("Critical configuration validation passed", ["AppSettingsService"]);
			return result;
		} catch (err) {
			Logger.error("Critical configuration validation failed", err, ["AppSettingsService"]);
			throw err;
		}
	}

	static getConfigSummary(): Record<string, any> {
		Logger.debug("Generating configuration summary", ["AppSettingsService"]);
		const summary: Record<string, any> = AppSettingsHelper.getConfigSummary();
		Logger.debug("Configuration summary generated", ["AppSettingsService"]);
		return summary;
	}

	static getRuntimeConfig(): AppSettingsSchema {
		Logger.debug("Runtime configuration requested", ["AppSettingsService"]);
		return AppSettingsHelper.getRuntimeConfig();
	}

	static getSearchBanner(): SearchBannerSettings | null {
		const config = this.getRuntimeConfig();
		if (config.search_banner_enabled !== "true" || !config.search_banner_text?.trim()) {
			return null;
		}

		const variant = config.search_banner_variant ?? "info";
		if (!isAppBannerVariant(variant)) {
			Logger.warn(`Unsupported search banner variant: ${variant}`, ["AppSettingsService"]);
			return null;
		}

		Logger.debug("Retrieved Search page banner configuration", ["AppSettingsService"]);
		return {
			text: config.search_banner_text.trim(),
			variant,
			dismissible: config.search_banner_dismissible === "true",
		};
	}
}
